import { useEffect, useState } from "react";
import { DefaultPacketPreview } from "@/components/DefaultPacketPreview";
import { VisitPacketPlan } from "@/components/VisitPacketPlan";
import { fmtDate } from "@/lib/status";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";

const LENGTHS = [10, 15, 20, 30, 45, 60, 90, 120];

function PatientSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    const t = setTimeout(() => api.get("/patients", { params: { q } }).then((r) => setRows(r.data.items || r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient name, DOB or chart ID" data-testid="appt-patient-search" />
      <div className="max-h-40 overflow-y-auto mt-1">{rows.slice(0, 8).map((p) => (
        <button key={p.id} type="button" className="w-full text-left text-sm px-2 py-1.5 hover:bg-slate-50 rounded" onClick={() => onPick(p)} data-testid={`appt-patient-${p.id}`}>
          {p.first_name} {p.last_name} <span className="text-slate-500">- {fmtDate(p.dob)} - {p.chart_id}</span></button>))}</div>
    </div>
  );
}

export default function AppointmentDialog({ open, onClose, initial, onSaved }) {
  const { practice } = usePractice();
  const [f, setF] = useState({});
  const [patient, setPatient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createPk, setCreatePk] = useState(null);
  const sel = createPk;
  useEffect(() => {
    if (open) {
      setF({ date: initial?.date, start: initial?.start || "09:00", duration_min: 30, visit_type: "", provider_code: initial?.provider_code || practice.providers[0]?.code,
        facility_code: initial?.facility_code || practice.facilities[0]?.code });
      setPatient(null);
      setCreatePk(null);
    }
  }, [open, initial, practice]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", { ...f, patient_id: patient.id, create_packet: !!sel?.length, template_ids: sel || [] });
      toast.success(data.packet?.status === "created" ? `Appointment scheduled - packet created (${data.packet.forms.join(", ")})` : "Appointment scheduled");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pickSel = (k, list, tid) => (
    <Select value={f[k] || ""} onValueChange={(v) => setF({ ...f, [k]: v })}><SelectTrigger className="mt-1" data-testid={tid}><SelectValue placeholder="Choose" /></SelectTrigger>
      <SelectContent>{list.map((x) => <SelectItem key={x.code} value={x.code}>{x.name}</SelectItem>)}</SelectContent></Select>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle>Schedule a patient</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {patient ? <div className="flex justify-between bg-brand-soft rounded p-2 text-sm" data-testid="appt-selected-patient"><span><b>{patient.first_name} {patient.last_name}</b> - {fmtDate(patient.dob)}</span><button className="underline text-xs" onClick={() => { setPatient(null); setCreatePk(null); }} data-testid="appt-change-patient">Change</button></div>
            : <PatientSearch onPick={setPatient} />}
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Date</Label><Input type="date" className="mt-1" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="appt-date" /></div>
            <div><Label>Start</Label><Input type="time" className="mt-1" value={f.start || ""} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="appt-start" /></div>
            <div><Label>Length</Label>
              <Select value={String(f.duration_min || 30)} onValueChange={(v) => setF({ ...f, duration_min: Number(v) })}><SelectTrigger className="mt-1" data-testid="appt-length"><SelectValue /></SelectTrigger>
                <SelectContent>{LENGTHS.map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-2"><div><Label>Provider</Label>{pickSel("provider_code", practice.providers, "appt-provider")}</div><div><Label>Facility</Label>{pickSel("facility_code", practice.facilities, "appt-facility")}</div></div>
          {open && (patient ? <VisitPacketPlan patientId={patient.id} patientName={`${patient.first_name} ${patient.last_name}`} facility={f.facility_code} provider={f.provider_code} sel={sel} setSel={setCreatePk} />
            : <div><DefaultPacketPreview facility={f.facility_code} provider={f.provider_code} testid="appt-default-packet" />
                <p className="text-xs text-slate-500 mt-1" data-testid="appt-pick-patient-hint">Pick a patient to see their signing history and choose the forms for this visit.</p></div>)}
          <div><Label>Visit type</Label><Input className="mt-1" value={f.visit_type || ""} onChange={(e) => setF({ ...f, visit_type: e.target.value })} placeholder="e.g. Follow-up" data-testid="appt-visit-type" /></div>
          <Button className="btn-brand w-full" disabled={busy || !patient || !f.date || !f.start || createPk === null} onClick={save} data-testid="appt-save">Save appointment</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
